// ---------------------------------------------------------------------------
// Template → Service Category mapping (dynamic, driven by the live RFI list)
//
// Templates from HyperComply do NOT carry a service_category field, so we
// resolve it client-side: strip the "Diligent " prefix from the template
// name and look it up in the RFI list (`UseGetRfiListForFilters`). The
// matched RFI item carries `rfi_grouping` (the group/category bucket) and
// `rfi_color_code_*` (the dot color).
//
// Anything that doesn't follow the "Diligent <RFI name>" convention falls
// into the "Custom" group at the bottom of the dropdown.
// ---------------------------------------------------------------------------

const DILIGENT_PREFIX = "Diligent ";

// Strip "Diligent " from a template name and return the candidate SC name
const stripDiligentPrefix = (templateLabel) => {
  if (!templateLabel || !templateLabel.startsWith(DILIGENT_PREFIX)) return null;
  return templateLabel.slice(DILIGENT_PREFIX.length).trim();
};

/**
 * Find the RFI entry whose name matches a template's SC candidate.
 * Case-insensitive exact-name match.
 */
const findRfiForTemplate = (templateLabel, rfiList) => {
  const candidate = stripDiligentPrefix(templateLabel);
  if (!candidate || !rfiList || rfiList.length === 0) return null;
  const lower = candidate.toLowerCase();
  return (
    rfiList.find((r) => (r?.rfi_name || "").toLowerCase() === lower) || null
  );
};

/**
 * Resolve the group name for a template from the live RFI list.
 * Returns null if the template can't be mapped (will go to Custom bucket).
 */
export const getTemplateGroup = (templateLabel, rfiList) => {
  const rfi = findRfiForTemplate(templateLabel, rfiList);
  if (!rfi) return null;
  // rfi_grouping is semicolon-separated (e.g. "CRO;Technology") — take first
  const grouping = (rfi.rfi_grouping || "").split(";").map((g) => g.trim()).filter(Boolean);
  return grouping[0] || null;
};

/**
 * Resolve the inline-style colors for a template from the matched RFI.
 * Returns { bg, text, border } hex strings, or a neutral gray fallback.
 */
export const getTemplateColors = (templateLabel, rfiList) => {
  const rfi = findRfiForTemplate(templateLabel, rfiList);
  if (!rfi) {
    return { bg: "#F3F4F6", text: "#6B7280", border: "#9CA3AF" };
  }
  return {
    bg: rfi.rfi_color_code_bg || "#EEF2FF",
    text: rfi.rfi_color_code_text || "#4338CA",
    border: rfi.rfi_color_code_border || "#4338CA",
  };
};

/**
 * Group a flat list of template options using the live RFI list.
 *
 * Group order is derived from the RFI list itself: every distinct group
 * encountered in `rfi_grouping` (in the order it first appears in the
 * RFI list), with "Foundation" pinned first if present, then any custom
 * bucket appended at the end.
 *
 * @param {Array<{value, label, ...}>} options - flat template list
 * @param {Array} rfiList - raw RFI list from UseGetRfiListForFilters
 * @returns {Array} flat list with isGroupHeader entries between groups
 */
export const buildGroupedTemplateOptions = (options, rfiList) => {
  if (!options || options.length === 0) return [];

  // Bucket templates by resolved group
  const grouped = {};
  const customBucket = [];

  options.forEach((opt) => {
    const group = getTemplateGroup(opt.label, rfiList);
    if (group) {
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(opt);
    } else {
      customBucket.push(opt);
    }
  });

  // Derive group order from rfiList: first appearance per group, Foundation first
  const groupOrder = [];
  const seenGroups = new Set();
  (rfiList || []).forEach((rfi) => {
    (rfi?.rfi_grouping || "")
      .split(";")
      .map((g) => g.trim())
      .filter(Boolean)
      .forEach((g) => {
        if (!seenGroups.has(g)) {
          seenGroups.add(g);
          groupOrder.push(g);
        }
      });
  });
  groupOrder.sort((a, b) => {
    if (a === "Foundation") return -1;
    if (b === "Foundation") return 1;
    return 0;
  });

  const result = [];
  const groupMembers = {};

  const pushGroup = (groupName, members) => {
    const sorted = [...members].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
    groupMembers[groupName] = sorted.map((o) => o.value);
    result.push({
      value: `__group__${groupName}`,
      label: groupName,
      isGroupHeader: true,
      groupName,
    });
    sorted.forEach((opt) =>
      result.push({
        ...opt,
        colors: getTemplateColors(opt.label, rfiList),
        groupName,
      })
    );
  };

  // 1. Render groups in derived order
  groupOrder.forEach((group) => {
    if (grouped[group]) pushGroup(group, grouped[group]);
  });

  // 2. Catch any group present in templates but missing from groupOrder
  Object.keys(grouped).forEach((group) => {
    if (!groupOrder.includes(group)) pushGroup(group, grouped[group]);
  });

  // 3. Custom group at the bottom (everything that didn't map)
  if (customBucket.length > 0) {
    pushGroup("Custom", customBucket);
  }

  result.forEach((opt) => {
    if (opt.isGroupHeader) opt.groupMembers = groupMembers[opt.groupName] || [];
  });

  return result;
};
